// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { Lock, Eye, EyeOff, ShieldAlert } from 'lucide-react'
import * as api from '../../api/client'

// A field as returned by GET /field-catalog
export interface CatalogField {
  owlrx_table: string
  owlrx_column: string
  label: string
  is_phi: boolean
  default_access: 'full' | 'masked' | 'hidden'
  maskable: boolean
  default_mask: string | null
}
export interface MaskStyle { value: string; label: string }
export type Access = 'full' | 'masked' | 'hidden'
export interface FieldRule { owlrx_table: string; owlrx_column: string; access: Access; mask_style?: string | null }

// Friendlier entity names for grouping.
const TABLE_LABELS: Record<string, string> = {
  patients: 'Patient',
  rx_items: 'Prescription',
  rx_claims: 'Insurance claim',
  pioneerrx_patient_detail: 'Patient detail',
  patient_addresses: 'Patient address',
  pioneerrx_prescribers: 'Prescriber',
  pioneerrx_prescriber_addresses: 'Prescriber address',
  rx_transfers: 'Transfer',
  pioneerrx_employees_touched: 'Employees',
}

// Sample values for the live preview, per mask style.
function sampleFor(f: CatalogField): string {
  const c = f.owlrx_column.toLowerCase()
  if (c.includes('ssn')) return 'XXX-XX-6789'
  if (c.includes('birth') || c === 'dob') return '1985-03-14'
  if (c.includes('email')) return 'jane.doe@example.com'
  if (c.includes('first_name')) return 'Jane'
  if (c.includes('last_name') || c.includes('display_name')) return 'Doe'
  if (c.includes('phone')) return '555-867-5309'
  if (c.includes('zip')) return '90210'
  if (c.includes('dea')) return 'BD1234563'
  return 'Sample'
}
// Mirror of the server FieldRedactor masking (preview only).
function previewMask(value: string, style?: string | null): string {
  if (!value) return value
  switch (style) {
    case 'last4': return value.length <= 4 ? '•'.repeat(value.length) : '•'.repeat(value.length - 4) + value.slice(-4)
    case 'first_initial': return value[0] + '•'.repeat(Math.max(1, value.length - 1))
    case 'year_only': { const m = value.match(/\b(\d{4})\b/); return m ? m[1] : '••••' }
    case 'redact_all_but_domain': { const at = value.indexOf('@'); return at > 0 ? '•••' + value.slice(at) : '•••' }
    default: return '••••••'
  }
}

interface Props {
  roleId: string | null          // null = new role (rules saved after create)
  disabled: boolean
  // The editor reads current rules from here on save.
  onRulesChange: (rules: FieldRule[]) => void
}

export default function FieldAccessSection({ roleId, disabled, onRulesChange }: Props) {
  const [catalog, setCatalog] = useState<CatalogField[]>([])
  const [maskStyles, setMaskStyles] = useState<MaskStyle[]>([])
  const [rules, setRules] = useState<Record<string, FieldRule>>({})  // key = table.column
  const [loading, setLoading] = useState(true)
  const [phiOnly, setPhiOnly] = useState(true)
  const [search, setSearch] = useState('')

  const keyOf = (t: string, c: string) => `${t}.${c}`

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const cat = await api.apiFetch(`/field-catalog`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : Promise.reject(r))
        if (cancelled) return
        setCatalog(cat.fields ?? [])
        setMaskStyles(cat.mask_styles ?? [])

        // Existing rules (edit mode). New roles start from catalog defaults.
        const initial: Record<string, FieldRule> = {}
        if (roleId) {
          const rr = await api.apiFetch(`/roles/${roleId}/field-rules`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : { rules: [] })
          if (cancelled) return
          for (const r of (rr.rules ?? []))
            initial[keyOf(r.owlrx_table, r.owlrx_column)] = r
        }
        setRules(initial)
        setLoading(false)
      } catch {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [roleId])

  // The effective access for a field = explicit rule, else catalog default.
  function effective(f: CatalogField): FieldRule {
    const k = keyOf(f.owlrx_table, f.owlrx_column)
    if (rules[k]) return rules[k]
    return { owlrx_table: f.owlrx_table, owlrx_column: f.owlrx_column,
             access: f.default_access, mask_style: f.default_mask }
  }

  function setAccess(f: CatalogField, access: Access) {
    if (disabled) return
    const k = keyOf(f.owlrx_table, f.owlrx_column)
    const next = { ...rules }
    const mask = access === 'masked' ? (rules[k]?.mask_style || f.default_mask || 'fixed_stars') : null
    next[k] = { owlrx_table: f.owlrx_table, owlrx_column: f.owlrx_column, access, mask_style: mask }
    setRules(next)
    onRulesChange(Object.values(next))
  }
  function setMask(f: CatalogField, mask_style: string) {
    if (disabled) return
    const k = keyOf(f.owlrx_table, f.owlrx_column)
    const next = { ...rules, [k]: { owlrx_table: f.owlrx_table, owlrx_column: f.owlrx_column, access: 'masked' as Access, mask_style } }
    setRules(next)
    onRulesChange(Object.values(next))
  }
  function maskAllPhi() {
    if (disabled) return
    const next = { ...rules }
    for (const f of catalog)
      if (f.is_phi)
        next[keyOf(f.owlrx_table, f.owlrx_column)] =
          { owlrx_table: f.owlrx_table, owlrx_column: f.owlrx_column, access: 'masked', mask_style: f.default_mask || 'fixed_stars' }
    setRules(next)
    onRulesChange(Object.values(next))
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return catalog.filter(f =>
      (!phiOnly || f.is_phi) &&
      (!q || f.label.toLowerCase().includes(q) || f.owlrx_column.toLowerCase().includes(q)))
  }, [catalog, phiOnly, search])

  const grouped = useMemo(() => {
    const out: Record<string, CatalogField[]> = {}
    for (const f of filtered) {
      const g = TABLE_LABELS[f.owlrx_table] ?? f.owlrx_table
      if (!out[g]) out[g] = []
      out[g].push(f)
    }
    return out
  }, [filtered])

  const phiCount = catalog.filter(f => f.is_phi).length

  if (loading) return <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Loading field catalog…</div>

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        Field access &amp; PHI masking
      </label>
      <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '0 0 10px' }}>
        Choose what this role can see per field. Fields marked <PhiBadge /> contain PHI and default to masked.
        Pick <strong>Full</strong>, <strong>Masked</strong>, or <strong>Hidden</strong>; masked fields show a sample preview.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          type="search" placeholder="Search fields…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 140, padding: '5px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13 }}
        />
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={phiOnly} onChange={e => setPhiOnly(e.target.checked)} />
          PHI only ({phiCount})
        </label>
        <button type="button" onClick={maskAllPhi} disabled={disabled}
          style={{ fontSize: 12, padding: '5px 10px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-bg)', cursor: disabled ? 'default' : 'pointer' }}>
          Mask all PHI
        </button>
      </div>

      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
        {Object.entries(grouped).map(([group, fields]) => (
          <div key={group}>
            <div style={{ position: 'sticky', top: 0, background: 'var(--color-bg)', padding: '6px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)' }}>
              {group}
            </div>
            {fields.map(f => {
              const eff = effective(f)
              const sample = sampleFor(f)
              return (
                <div key={keyOf(f.owlrx_table, f.owlrx_column)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--color-border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {f.label}{f.is_phi && <PhiBadge />}
                    </div>
                    {eff.access === 'masked' && (
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>
                        {sample} → {previewMask(sample, eff.mask_style)}
                      </div>
                    )}
                  </div>
                  <AccessToggle value={eff.access} disabled={disabled} onChange={a => setAccess(f, a)} />
                  {eff.access === 'masked' && f.maskable && (
                    <select value={eff.mask_style ?? 'fixed_stars'} disabled={disabled}
                      onChange={e => setMask(f, e.target.value)}
                      style={{ fontSize: 12, padding: '3px 6px', border: '1px solid var(--color-border)', borderRadius: 6 }}>
                      {maskStyles.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  )}
                </div>
              )
            })}
          </div>
        ))}
        {Object.keys(grouped).length === 0 && (
          <div style={{ padding: 16, fontSize: 13, color: 'var(--color-text-muted)' }}>No fields match.</div>
        )}
      </div>
    </div>
  )
}

function PhiBadge() {
  return (
    <span title="Contains PHI" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--color-danger)', borderRadius: 4, padding: '1px 5px' }}>
      <ShieldAlert size={10} aria-hidden="true" /> PHI
    </span>
  )
}

function AccessToggle({ value, disabled, onChange }: { value: Access; disabled: boolean; onChange: (a: Access) => void }) {
  const opts: { v: Access; label: string; icon: any }[] = [
    { v: 'full', label: 'Full', icon: Eye },
    { v: 'masked', label: 'Masked', icon: Lock },
    { v: 'hidden', label: 'Hidden', icon: EyeOff },
  ]
  return (
    <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
      {opts.map(o => {
        const on = value === o.v
        const Icon = o.icon
        return (
          <button key={o.v} type="button" disabled={disabled} onClick={() => onChange(o.v)}
            title={o.label}
            style={{
              display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, padding: '4px 7px', border: 'none',
              background: on ? 'var(--color-primary)' : 'transparent',
              color: on ? '#fff' : 'var(--color-text-muted)',
              cursor: disabled ? 'default' : 'pointer',
            }}>
            <Icon size={11} aria-hidden="true" /> {o.label}
          </button>
        )
      })}
    </div>
  )
}
